import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixSocialGroupsApiV2MembershipQuestionsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/questions-proxy',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/questions-proxy',
        destPath: '',
      },
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
    ],
    'editor-flow.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
    ],
    'groups.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
      {
        srcPath: '/social-groups-proxy/api/questions',
        destPath: '',
      },
      {
        srcPath: '/_api/questions-proxy',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/questions',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/social-groups-proxy/questions',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_groups_membership-questions';

/**
 * Retrieves the membership questions for a group.
 *
 * >**Note:**
 * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
 */
export function listMembershipQuestions(
  payload: object
): RequestOptionsFactory<any> {
  function __listMembershipQuestions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.membership_question',
      method: 'GET' as any,
      methodFqn:
        'wix.social.groups.api.v2.MembershipQuestionsService.ListMembershipQuestions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2MembershipQuestionsServiceUrl({
        protoPath: '/v2/membership-questions/{groupId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),

      fallback: [
        {
          method: 'GET' as any,
          url: resolveWixSocialGroupsApiV2MembershipQuestionsServiceUrl({
            protoPath: '/v2/membership-questions/{groupId}',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __listMembershipQuestions;
}

/**
 * Creates membership questions if none have been set up. Otherwise, replaces all existing questions.
 *
 * Providing an empty array means that members won't have to answer any question when joining the group.
 * Only admins can create or replace membership questions.
 *
 * >**Note:**
 * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
 */
export function createOrReplaceAllMembershipQuestions(
  payload: object
): RequestOptionsFactory<any> {
  function __createOrReplaceAllMembershipQuestions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.membership_question',
      method: 'PUT' as any,
      methodFqn:
        'wix.social.groups.api.v2.MembershipQuestionsService.CreateOrReplaceAllMembershipQuestions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2MembershipQuestionsServiceUrl({
        protoPath: '/v2/membership-questions/{groupId}',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __createOrReplaceAllMembershipQuestions;
}

/**
 * Retrieves the answers to the membership questions, given the provided filters.
 *
 * >**Note:**
 * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/rest/articles/getting-started/access-types-and-permissions).
 */
export function listAnswers(payload: object): RequestOptionsFactory<any> {
  function __listAnswers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.membership_question',
      method: 'POST' as any,
      methodFqn:
        'wix.social.groups.api.v2.MembershipQuestionsService.ListAnswers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2MembershipQuestionsServiceUrl({
        protoPath: '/v2/membership-questions/{groupId}/answers',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __listAnswers;
}
